import { extname } from 'loose-path';

const IS_ANDROID = /android/i.test(navigator.userAgent);
const IS_FIREFOX = /firefox/i.test(navigator.userAgent);

// eslint-disable-next-line @typescript-eslint/no-explicit-any, no-restricted-globals
const IS_IOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(self as any).MSStream;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const IS_WKWEBVIEW = Boolean((window as any).webkit?.messageHandlers);

// Android Scene Viewer is a feature of ARCore 1.9+.
// Support status must be checked against the device list (https://developers.google.com/ar/devices).
// According to this list, most popular recent devices are supported.
// Therefore, this feature is enabled on all android devices.
const IS_SUPPORTED_SCENE_VIEWER = IS_ANDROID && !IS_FIREFOX;

// iOS Quick Look is a feature of Safari available in iOS 12+.
// Other browsers, such as Chrome, also have ARCore built-in, so the AR feature will work.
// Therefore, it is enabled unconditionally on non-WebViews, and conditionally on WebViews.
const IS_SUPPORTED_AR_QUICK_LOOK =
  IS_IOS && (IS_WKWEBVIEW || Boolean(document.createElement('a').relList.supports('ar')));

interface ArViewerCommonOptions {
  scale?: 'auto' | 'fixed';
}

function createSceneViewerUrl(glb: string, { scale }: ArViewerCommonOptions = {}): URL {
  const url = new URL('intent://arvr.google.com/scene-viewer/1.0');
  url.searchParams.set('mode', 'ar_preferred');
  url.searchParams.set('disable_occlusion', 'true');
  if (scale === 'fixed') {
    url.searchParams.set('resizable', 'false');
  }
  url.searchParams.set('file', String(new URL(glb, window.location.toString())));
  url.hash =
    '#Intent;scheme=https;package=com.google.ar.core;action=android.intent.action.VIEW;end';
  return url;
}

function createArQuickLookUrl(usdz: string, { scale }: ArViewerCommonOptions = {}): URL {
  const url = new URL(usdz, window.location.toString());
  if (scale === 'fixed') {
    if (url.hash) {
      url.hash += '&';
    }
    url.hash += 'allowsContentScaling=0';
  }
  return url;
}

export interface ArViewerData extends ArViewerCommonOptions {
  glb?: string | File;
  usdz?: string | File;
}

function isRemoteUrl(urlString: string, ext?: string): boolean {
  try {
    const url = new URL(urlString, window.location.toString());
    return /^https?:$/.test(url.protocol) && extname(url.pathname) === ext;
  } catch (e) {
    // noop
  }
  return false;
}

function clickAnchor(attrs: Record<string, string>): void {
  const anchor = document.createElement('a');
  anchor.style.display = 'none';
  Object.keys(attrs).forEach((key) => {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    anchor.setAttribute(key, attrs[key]!);
  });
  const img = document.createElement('img'); // Hack for Quick Look. Skip the download view by inserting img.
  anchor.appendChild(img);
  anchor.click();
  anchor.removeChild(img);
}

export function createArViewerRunner({ glb, usdz, ...options }: ArViewerData): (() => void) | null {
  if (IS_SUPPORTED_AR_QUICK_LOOK && typeof usdz === 'string' && isRemoteUrl(usdz, '.usdz')) {
    return (): void => {
      clickAnchor({
        rel: 'ar',
        href: String(createArQuickLookUrl(usdz, options)),
      });
    };
  }
  if (IS_SUPPORTED_SCENE_VIEWER && typeof glb === 'string' && isRemoteUrl(glb, '.glb')) {
    return (): void => {
      clickAnchor({
        href: String(createSceneViewerUrl(glb, options)),
      });
    };
  }
  return null;
}
